// Auth utilities with backend API integration
import axios from 'axios';
import { createApiClient } from './httpClient';

// ✅ REMOVED: TOKEN_KEY (token now in HttpOnly cookie, not accessible to JS)
const USER_KEY = "auth_user";
const ROLE_ID_KEY = "auth_role_id";
const PERMISSIONS_KEY = "auth_permissions";
const RBAC_DEBUG = process.env.REACT_APP_RBAC_DEBUG !== "false";

// ✅ Use environment variable (fallback to localhost for safety)
const API_BASE_URL = process.env.REACT_APP_REST_BASE_URL || "http://localhost:8000/api";

const apiClient = createApiClient({
  baseURL: `${API_BASE_URL}/auth`,
  headers: {
    "Content-Type": "application/json",
  },
});

const summarizePermissions = (permissions = []) => {
  if (!Array.isArray(permissions)) return { count: 0, invalidShape: true };

  return {
    count: permissions.length,
    missingResource: permissions.filter((perm) => !perm?.resource).length,
    missingAction: permissions.filter((perm) => !perm?.action).length,
    resources: [...new Set(permissions.map((perm) => perm?.resource?.displayName || perm?.resource?.resourceKey).filter(Boolean))],
    actions: [...new Set(permissions.map((perm) => perm?.action?.name).filter(Boolean))],
  };
};

export const hasUsablePermissionData = (permissions) => {
  return Array.isArray(permissions) && permissions.some((perm) => perm?.resource && perm?.action);
};

const logRBAC = (message, data) => {
  if (RBAC_DEBUG) {
    console.log(`[RBAC] ${message}`, data || "");
  }
};

export const isAuthenticated = () => {
  try {
    // ✅ CHANGED: Check if user exists (token is in HttpOnly cookie, not accessible)
    return Boolean(localStorage.getItem(USER_KEY));
  } catch (e) {
    return false;
  }
};

// ✅ REMOVED: getToken() - token in HttpOnly cookie, not accessible to JavaScript

export const getUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const getRoleId = () => {
  try {
    return localStorage.getItem(ROLE_ID_KEY);
  } catch (e) {
    return null;
  }
};

export const getPermissions = () => {
  try {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    const permissions = raw ? JSON.parse(raw) : null;
    if (raw && !Array.isArray(permissions)) {
      console.warn("[RBAC] Ignoring cached permissions because the shape is not an array", permissions);
      return null;
    }
    logRBAC("Cached permissions read", summarizePermissions(permissions || []));
    return permissions;
  } catch (e) {
    console.warn("[RBAC] Failed to parse cached permissions; clearing stale cache", e);
    localStorage.removeItem(PERMISSIONS_KEY);
    return null;
  }
};

// ✅ REMOVED: isTokenExpired() - handled server-side with cookie expiration

export const setSession = (user, roleId = null, permissions = null) => {
  try {
    // ✅ REMOVED: localStorage.setItem(TOKEN_KEY, token) - token in HttpOnly cookie
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (roleId) localStorage.setItem(ROLE_ID_KEY, roleId);
    if (permissions) localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
  } catch (e) { }
};

export const clearSession = () => {
  try {
    // ✅ REMOVED: localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ROLE_ID_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem("auth_type");
  } catch (e) { }
};

export const signup = async ({ firstName, lastName, email, contactNumber, password, captchaToken }) => {
  try {
    const response = await apiClient.post("/signup", {
      firstName,
      lastName,
      email,
      contactNumber,
      password,
      captchaToken
    });

    if (response.status === 201 || response.status === 200) {
      const fullName = `${firstName} ${lastName}`.trim();
      localStorage.setItem('signup_name', fullName);
      localStorage.setItem('signup_email', email);
      return { success: true, message: response.data.message };
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "Signup failed";
    throw new Error(errorMessage);
  }
};

export const login = async ({ email, password, captchaToken }) => {
  try {
    const response = await apiClient.post('/login', {
      email,
      password,
      captchaToken
    });

    if (response.status === 200) {
      const { userId, roleId } = response.data;
      // ✅ REMOVED: access_token from response
      const signupName = localStorage.getItem('signup_name') || email.split('@')[0];
      const user = { email, userId, name: signupName };

      // ✅ CHANGED: Don't pass token to setSession
      setSession(user, roleId);
      localStorage.setItem("auth_type", "local");

      // ✅ REMOVED: accessToken from return
      return { success: true, message: response.data.message, userId, roleId };
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "Login failed";
    throw new Error(errorMessage);
  }
};

export const fetchUserPermissions = async (userId, retryCount = 0) => {
  try {
    logRBAC("Fetching permissions", { userId, retryCount });
    // ✅ CHANGED: Use apiClient which sends cookies automatically
    const response = await apiClient.get(
      `/access/${userId}`
    );

    if (response.status === 200) {
      const permissions = response.data.permissions || [];
      logRBAC("Access API response", {
        userId: response.data.userId,
        role_id: response.data.role_id,
        summary: summarizePermissions(permissions),
        sample: permissions.slice(0, 3),
      });

      // If no permissions returned and we haven't retried yet, wait and retry
      if (!permissions || permissions.length === 0) {
        if (retryCount < 2) {
          console.log(`[RBAC] No permissions returned, retrying... (attempt ${retryCount + 1})`);
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
          return fetchUserPermissions(userId, retryCount + 1);
        }
      }

      if (permissions.length > 0 && !hasUsablePermissionData(permissions)) {
        console.warn(
          "[RBAC] Permissions were returned, but none include resource/action details. Route guards will deny access.",
          summarizePermissions(permissions)
        );
      }

      localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
      return permissions;
    }
  } catch (error) {
    console.error('Failed to fetch permissions:', error);
    return [];
  }
};

export const forgotPassword = async ({ email }) => {
  try {
    const response = await apiClient.post("/forgot-password", { email });

    if (response.status === 200) {
      return { success: true, message: response.data.message };
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "Failed to send reset link";
    throw new Error(errorMessage);
  }
};

export const resetPassword = async ({ token, new_password }) => {
  try {
    const response = await apiClient.post("/reset-password", {
      token,
      new_password,
    });

    if (response.status === 200) {
      return { success: true, message: response.data.message };
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      "Password reset failed";
    throw new Error(errorMessage);
  }
};

export const ssoLogin = async (code) => {
  try {
    console.log('Calling SSO login with code:', code);
    console.log('API Base URL:', API_BASE_URL);

    // ✅ CHANGED: Use fetch with credentials: 'include' for cookies
    const response = await fetch(
      `${API_BASE_URL}/auth/sso-login?code=${code}`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include',  // ✅ Enable cookie sending
      }
    );

    console.log('SSO Response Status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('SSO Error Response:', errorText);
      let errorMessage = 'SSO login failed';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.detail || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('SSO Response Data:', data);

    const { userId, roleId, email } = data;
    // ✅ REMOVED: access_token (now in HttpOnly cookie)
    const user = { email: email || '', userId, name: '' };

    // ✅ CHANGED: Don't pass token to setSession
    setSession(user, roleId);
    localStorage.setItem("auth_type", "sso");

    return { success: true, message: data.message, userId, roleId, email };
    // ✅ REMOVED: accessToken from return
  } catch (error) {
    console.error('SSO Login Error:', error);
    const errorMessage = error.message || 'SSO login failed';
    throw new Error(errorMessage);
  }
};

export const logout = async () => {
  try {
    // ✅ CHANGED: Use apiClient which sends cookies automatically
    await apiClient.post('/logout');
  } catch (error) {
    // Even if logout API fails, we still clear the session locally
    console.error('Logout API error:', error);
  } finally {
    // Always clear session regardless of API response
    clearSession();
  }
};

export const fetchUserById = async () => {
  try {
    // ✅ CHANGED: Use apiClient which sends cookies automatically
    const user = getUser(); // only for userId

    if (!user?.userId) return null;

    const response = await apiClient.get(
      `/user/${user.userId}`
    );

    return response.data;
  } catch (error) {
    console.error("Failed to fetch user:", error);
  
    if (error.response?.status === 401) {
      clearSession();
    }
  
    return null;
  }
};
export default {
  isAuthenticated,
  getUser,
  getRoleId,
  getPermissions,
  setSession,
  clearSession,
  signup,
  login,
  ssoLogin,
  forgotPassword,
  resetPassword,
  logout,
  fetchUserPermissions,
  fetchUserById,
};
